import { ARCHIVED_BANNER_HEIGHT } from "components/archived-project-banner";
import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Radio } from "antd";
import InputNumberWithAddon from "components/input-number-with-addon";

import TextArea from "antd/lib/input/TextArea";
import { TProject } from "components/project-list-view/models";
import { useLazyQuery, useQuery } from "@apollo/client";
import { ProjectContext } from "context/ProjectProvider";
import { SubscriptionContext } from "context/SubscriptionProvider";
import {
  QUERY_PROJECT_AGG,
  QUERY_PROJECT_PHASES
} from "services/graphQL/queries";
import { DATE_FORMAT_MMDDYYYY, DateUtils, TDateTimeObj } from "utils/dateutils";
import { formatNumberWithCommas } from "utils/utils";
import CIQDatePicker from "components/custom-date-picker";
import { ErrorMessages } from "../../../constants";

type EditProjectDetailProps = {
  projectInfo: TProject;
  updateProjectData: (data: Record<string, any>) => Promise<void>;
  hasEditPermission: boolean;
};

type FormData = {
  number: string;
  name: string;
  client: string;
  start_date: TDateTimeObj;
  end_date: TDateTimeObj;
  value: number;
  details: string;
  address: string;
  phase?: number;
};

type ProjectPhase = {
  id: number;
  value: string;
};

function ProjectDetailEdit({
  projectInfo,
  updateProjectData,
  hasEditPermission
}: EditProjectDetailProps) {
  const [form] = Form.useForm();
  const [isDisableEditing, setFieldsDisabled] = useState(true);
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);

  const { subscriptionId } = useContext(SubscriptionContext);
  const { gqlClientForProject, isFeatureFlagEnabled, isProjectArchived } =
    useContext(ProjectContext);
  const showProjectStage = isFeatureFlagEnabled("BUYOUT");
  const [checkForExistingProject] = useLazyQuery(QUERY_PROJECT_AGG);
  const { data: projectPhaseData } = useQuery(QUERY_PROJECT_PHASES, {
    client: gqlClientForProject,
    skip: !showProjectStage || !gqlClientForProject
  });
  const projectPhases: ProjectPhase[] = projectPhaseData?.project_phase ?? [];
  const constructionPhaseId = projectPhases.find(
    (phase) => phase.value === "Construction"
  )?.id;

  const configuredPhaseId = projectInfo.project_phase?.id;

  useEffect(() => {
    if (configuredPhaseId != null) {
      form.setFieldsValue({ phase: configuredPhaseId });
      return;
    }
    if (constructionPhaseId != null) {
      form.setFieldsValue({ phase: constructionPhaseId });
    }
  }, [configuredPhaseId, constructionPhaseId, form]);

  const onFinishForm = async (data: FormData) => {
    setSubmitInProgress(true);
    const body: Record<string, unknown> = {
      number: data.number,
      name: data.name,
      client: data.client,
      start_date: data.start_date ? DateUtils.format(data.start_date) : null,
      end_date: data.end_date ? DateUtils.format(data.end_date) : null,
      value: data.value || null,
      details: data.details,
      address: data.address
    };
    if (showProjectStage) {
      const selectedPhase = data.phase ?? constructionPhaseId;
      if (selectedPhase != null) {
        body.phase = selectedPhase;
      }
    }
    await updateProjectData(body);
    setSubmitInProgress(false);
    setFieldsDisabled(true);
  };

  const formRules = {
    name: [
      {
        required: true,
        validateTrigger: "onSubmit",
        message: ErrorMessages.ProjectNameMsg
      },
      () => ({
        async validator(_: any, value: string) {
          if (value && value?.length > 0 && value !== projectInfo.name) {
            const response: any = await checkForExistingProject({
              variables: {
                where: {
                  // value is guaranteed truthy by the `value && value?.length > 0` guard above, so `|| ""` is unreachable
                  /* v8 ignore next */
                  name: { _eq: value || "" },
                  owner_subscription_id: {
                    _eq: subscriptionId
                  }
                }
              }
            });

            const projectCount =
              response?.data?.project_aggregate?.aggregate?.count ?? 0;
            if (projectCount > 0) {
              return Promise.reject(
                new Error(ErrorMessages.ProjectNameExistsMsg)
              );
            }
          }

          return Promise.resolve();
        }
      })
    ],
    number: [
      {
        required: true,
        validateTrigger: "onSubmit",
        message: ErrorMessages.ProjectNumberErrMsg
      },
      () => ({
        async validator(_: any, value: string) {
          if (value && value?.length > 0 && value !== projectInfo.number) {
            const response: any = await checkForExistingProject({
              variables: {
                where: {
                  id: { _neq: projectInfo.id },
                  // value is guaranteed truthy by the `value && value?.length > 0` guard above, so `|| ""` is unreachable
                  /* v8 ignore next */
                  number: { _eq: value || "" },
                  owner_subscription_id: {
                    _eq: subscriptionId
                  }
                }
              }
            });
            const projectCount =
              response?.data?.project_aggregate?.aggregate?.count ?? 0;
            if (projectCount) {
              return Promise.reject(new Error(ErrorMessages.ProjectNumberMsg));
            }
          }

          return Promise.resolve();
        }
      })
    ]
  };
  return (
    <div
      className="bg-white overflow-y-auto w-full px-10 md:px-20 lg:px-48 justify-center"
      style={{ height: `calc(100vh - 150px - ${ARCHIVED_BANNER_HEIGHT})` }}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          number: projectInfo.number,
          name: projectInfo.name,
          client: projectInfo.client,
          start_date: projectInfo.start_date
            ? DateUtils.dateTimeObj(projectInfo.start_date)
            : "",
          end_date: projectInfo.end_date
            ? DateUtils.dateTimeObj(projectInfo.end_date)
            : "",
          value: projectInfo.value,
          details: projectInfo.details,
          address: projectInfo.address,
          phase: projectInfo.project_phase?.id ?? constructionPhaseId
        }}
        onFinish={onFinishForm}
        className="w-full"
      >
        <div className="flex w-full justify-between py-4">
          <div className=" text-black font-semibold text-xl">Details</div>
          <div className="flex space-x-2">
            {isDisableEditing ? (
              <Button
                className="w-24"
                type="primary"
                onClick={() => setFieldsDisabled(false)}
                disabled={!hasEditPermission}
                title={
                  hasEditPermission
                    ? ""
                    : isProjectArchived
                      ? undefined
                      : ErrorMessages.PermissionNotGranted
                }
                data-testid="edit-project-detail-button"
              >
                Edit
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  className="w-24"
                  htmlType="reset"
                  disabled={isDisableEditing || submitInProgress}
                  onClick={() => {
                    setFieldsDisabled(true);
                    form.resetFields();
                  }}
                  data-testid="cancel-edit-project-detail-button"
                >
                  Cancel
                </Button>
                <Button
                  className="w-24"
                  type="primary"
                  htmlType="submit"
                  loading={submitInProgress}
                  disabled={isDisableEditing || submitInProgress}
                  data-testid="save-project-detail-button"
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-20 gap-y-2">
          <Form.Item name="name" label="Name" rules={formRules.name}>
            <Input
              type="text"
              disabled={isDisableEditing}
              data-testid="edit-project-name"
            />
          </Form.Item>
          <Form.Item name="number" label="Number" rules={formRules.number}>
            <Input
              type="text"
              disabled={isDisableEditing}
              data-testid="edit-project-number"
            />
          </Form.Item>

          <Form.Item name="client" label="Client">
            <Input
              type="text"
              disabled={isDisableEditing}
              data-testid="edit-project-client"
            />
          </Form.Item>

          <Form.Item name="value" label="Value">
            <InputNumberWithAddon
              className="w-full"
              addonBefore="$"
              min={0}
              formatter={(value) => formatNumberWithCommas(value)}
              disabled={isDisableEditing}
              data-testid="edit-project-value"
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Start date"
            data-testid="edit-project-start-date"
          >
            <CIQDatePicker
              className="w-full date-picker-with-icon"
              format={DATE_FORMAT_MMDDYYYY}
              disabled={isDisableEditing}
            />
          </Form.Item>

          <Form.Item
            name="end_date"
            label="End date"
            data-testid="edit-project-end-date"
          >
            <CIQDatePicker
              className="w-full date-picker-with-icon"
              format={DATE_FORMAT_MMDDYYYY}
              disabled={isDisableEditing}
            />
          </Form.Item>

          <Form.Item name="details" label="Details">
            <TextArea
              rows={3}
              disabled={isDisableEditing}
              data-testid="edit-project-details"
            />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <TextArea
              rows={3}
              disabled={isDisableEditing}
              data-testid="edit-project-address"
            />
          </Form.Item>

          {showProjectStage ? (
            <Form.Item
              name="phase"
              label={<span className="font-semibold">Project Stage</span>}
              data-testid="edit-project-stage"
            >
              <Radio.Group
                className="flex gap-x-12"
                disabled={isDisableEditing}
              >
                {projectPhases.map((phase) => (
                  <Radio key={phase.id} value={phase.id}>
                    <span className="form-field-heading-2">{phase.value}</span>
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          ) : null}
          {showProjectStage ? <div /> : null}

          <Form.Item
            label="Project Created By:"
            className="font-medium"
            data-testid="edit-project-created-by"
          >
            {`${projectInfo?.created_by_user?.first_name} ${
              projectInfo?.created_by_user?.last_name
            } on ${DateUtils.format(projectInfo?.created_at)}`}
          </Form.Item>
          <Form.Item
            label="Time Zone"
            className="font-medium"
            data-testid="edit-project-timezone"
          >
            {`${DateUtils.dateTimeObj().format("([GMT]Z)")} - ${
              projectInfo.timezone.name
            }`}
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}
export default ProjectDetailEdit;
